/** A tab button for a file, used by TabbedCodeEditor */
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body} from "@khanacademy/wonder-blocks-typography";

import DeleteFileModalLauncher from "./delete-file-modal-launcher.tsx";
import FilenameEntryForm from "./filename-entry-form.tsx";
import * as strings from "./strings.tsx";

import pencilIcon from "@phosphor-icons/core/bold/pencil-simple-bold.svg";
import xIcon from "@phosphor-icons/core/bold/x-bold.svg";

type Props = {
    // The name of the file that this tab represents.
    filename: string;
    // Whether this tab is selected.
    isSelected: boolean;
    // Called when the tab is clicked.
    onSelect: () => void;
    // Called when a key is pressed while the tab is focused.
    onKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
    // The set of filenames in the editor.
    filenames: Set<string>;
    // Called when a rename is aborted. Note that a rename can be aborted by
    // unfocusing the tab or switching tabs while renaming with an invalid
    // filename entered, so this callback should be used to alert screen readers
    // that the rename has been aborted.
    onRenameAborted?: () => void;
    // Called when a rename is confirmed. The new name is passed as an argument.
    // Note that a rename can be confirmed by unfocusing the tab or switching
    // tabs while renaming with a valid filename entered, so this callback
    // should alert screen readers that the rename has been confirmed.
    onRename?: (newName: string, blurEvent: React.FocusEvent | null) => void;
    // Called when the delete button is clicked.
    onDelete?: () => void;
};

function FileTab({
    filename,
    isSelected,
    onSelect,
    onKeyDown,
    filenames,
    onRenameAborted,
    onRename,
    onDelete,
    forwardedRef,
}: Props & {
    forwardedRef: React.ForwardedRef<HTMLButtonElement>;
}): React.ReactElement {
    const [isRenaming, setIsRenaming] = React.useState(false);

    const shouldFocusRef = React.useRef(false);

    const tabIndex = isSelected ? 0 : -1;
    const actionButtons: Array<React.ReactNode> = [];
    if (isSelected && onRename != null) {
        actionButtons.push(
            <IconButton
                key="rename"
                icon={pencilIcon}
                kind="tertiary"
                size="xsmall"
                aria-label={strings.renameFile(filename)}
                onClick={() => setIsRenaming(true)}
                style={styles.actionButton}
                tabIndex={tabIndex}
            />,
        );
    }
    if (onDelete != null) {
        actionButtons.push(
            <DeleteFileModalLauncher
                key="delete"
                filename={filename}
                onConfirm={onDelete}
            >
                {({openModal}) => (
                    <IconButton
                        icon={xIcon}
                        kind="tertiary"
                        size="xsmall"
                        aria-label={strings.deleteFile(filename)}
                        onClick={openModal}
                        style={styles.actionButton}
                        tabIndex={tabIndex}
                    />
                )}
            </DeleteFileModalLauncher>,
        );
    }

    const totalActionButtonsWidth =
        actionButtons.length * actionButtonSize +
        Math.max(actionButtons.length - 1, 0) * actionButtonsGap;

    if (isRenaming) {
        return (
            <FilenameEntryForm
                isSelected={isSelected}
                currentFilename={filename}
                filenames={filenames}
                onSubmit={(newName, blurEvent) => {
                    setIsRenaming(false);
                    onRename?.(newName, blurEvent);
                }}
                onCancel={(blurEvent) => {
                    setIsRenaming(false);
                    onRenameAborted?.();
                    if (blurEvent == null) {
                        shouldFocusRef.current = true;
                    }
                }}
            />
        );
    }

    const ref = (element: HTMLButtonElement | null) => {
        if (typeof forwardedRef === "function") {
            forwardedRef(element);
        } else if (forwardedRef != null) {
            forwardedRef.current = element;
        }

        if (shouldFocusRef.current) {
            element?.focus();
            shouldFocusRef.current = false;
        }
    };

    if (isSelected) {
        return (
            <View style={styles.tabContainer}>
                <button
                    role="tab"
                    key={filename}
                    className={css(styles.tab, styles.selectedTab)}
                    onKeyDown={onKeyDown}
                    tabIndex={0}
                    aria-selected={true}
                    ref={ref}
                >
                    <Body
                        style={[
                            styles.tabText,
                            styles.selectedTabText,
                            {marginRight: totalActionButtonsWidth},
                        ]}
                    >
                        {filename}
                    </Body>
                </button>
                <View style={styles.actionButtons}>{actionButtons}</View>
            </View>
        );
    }

    return (
        <View style={styles.tabContainer}>
            <button
                role="tab"
                key={filename}
                onClick={onSelect}
                className={css(styles.tab, styles.unselectedTab)}
                onKeyDown={onKeyDown}
                tabIndex={-1}
                aria-selected={false}
                ref={ref}
            >
                <Body
                    style={[
                        styles.tabText,
                        {marginRight: totalActionButtonsWidth},
                    ]}
                >
                    {filename}
                </Body>
            </button>
            <View style={styles.actionButtons}>{actionButtons}</View>
        </View>
    );
}

export default React.forwardRef<HTMLButtonElement, Props>((props, ref) => (
    <FileTab
        {...props}
        // the key ensures we immediately exit the renaming state when another
        // tab is selected
        key={`${props.isSelected}`}
        forwardedRef={ref}
    />
));

const actionButtonSize = spacing.medium_16;
const actionButtonsGap = spacing.xSmall_8;

const styles = StyleSheet.create({
    tabContainer: {
        display: "flex",
        flexDirection: "row",
        position: "relative",
        flexShrink: 0,
    },
    tab: {
        flexShrink: 0,
        borderRadius: 4,
        height: 22,
        paddingLeft: spacing.xSmall_8,
        paddingRight: spacing.xSmall_8,
        color: color.offBlack,
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",

        ":focus": {
            outline: "none",
            boxShadow: "none",
        },
    },
    unselectedTab: {
        backgroundColor: color.offBlack16,
        border: "none",

        ":hover": {
            boxShadow: `0 0 0 1px ${color.white}, 0 0 0 3px ${color.offBlack16}`,
        },
        ":focus": {
            boxShadow: `0 0 0 1px ${color.white}, 0 0 0 3px ${color.offBlack16}`,
        },
        ":active": {
            backgroundColor: color.offBlack32,
            boxShadow: `0 0 0 1px ${color.white}, 0 0 0 3px ${color.offBlack32}`,
        },
    },
    selectedTab: {
        backgroundColor: color.white,
        border: "1px solid #73767D",
        paddingLeft: spacing.xSmall_8 - 1,
        paddingRight: spacing.xSmall_8 - 1,

        ":hover": {
            borderWidth: 2,
            paddingLeft: spacing.xSmall_8 - 2,
            paddingRight: spacing.xSmall_8 - 2,
        },
        ":focus": {
            borderWidth: 2,
            paddingLeft: spacing.xSmall_8 - 2,
            paddingRight: spacing.xSmall_8 - 2,
        },
        ":active": {
            backgroundColor: color.offBlack8,
        },
    },
    tabText: {
        fontSize: 14,
        lineHeight: "20px",
    },
    selectedTabText: {
        fontWeight: "bold",
        ":hover": {
            paddingTop: 0,
        },
    },
    actionButtons: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        position: "absolute",
        top: 0,
        bottom: 0,
        right: 0,
        paddingRight: spacing.xxxSmall_4,
        gap: actionButtonsGap,
    },
    actionButton: {
        color: color.offBlack,
        width: actionButtonSize,
        height: actionButtonSize,
    },
});
